import {useId} from 'react';

import {useFloating} from '@pb/kit-component-floating';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

import type {Placement} from '@floating-ui/react';
import type {UseFloatingProps, UseFloatingReturnType, getFloatingElPropsReturnType} from '@pb/kit-component-floating';
import type {ComponentProps} from 'react';


export interface UseDropdownProps extends UseFloatingProps {
    /**
     * If show the floating on over or on click, true for hideOnBodyLeave
     * @deprecated It will be removed in 6 months 03-15-2026. Use `hoverEnabled` or `clickEnabled` instead.
     * */
    hoverable?: boolean,

    /**
     * Hide when mouse leave the floating, true by default, true for hoverable Dropdowns
     * */
    hideOnBodyLeave?: boolean,
}

type FilteredUseFloatingReturnType = Omit<UseFloatingReturnType, 'getFloatingElProps' | 'floatingElRef' | 'arrow'>;

interface getDropdownPropsReturnType extends getFloatingElPropsReturnType {
    arrow: UseFloatingReturnType['arrow'],
}

export interface UseDropdownReturnType extends FilteredUseFloatingReturnType {
    getDropdownProps: (props?: ComponentProps<'div'>) => getDropdownPropsReturnType,
    dropdownRef: UseFloatingReturnType['floatingElRef'],
}

const defaultAllowedPlacements: Placement[] = [
    'bottom-end',
    'bottom-start',
    'top-end',
    'top-start',
];

/**
 * A hook with main logic for Dropdown component.
 * Can be used outside of Dropdown component, with DropdownBase component or with custom Floating component. In this case consider wrap it with FloatingTree component if nested Dropdowns are used.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-dropdown--documentation}
 * */
export const useDropdown = ({
    maxHeight = 300,
    maxWidth = 400,
    allowedPlacements = defaultAllowedPlacements,
    shown,
    setShown,
    hideOnBodyLeave = true,
    hoverable,
    clickEnabled = !hoverable,
    hoverEnabled,
    arrowEnabled = true,
    ...restProps
}: UseDropdownProps): UseDropdownReturnType => {
    const [open, setOpen] = useControlledState(shown, setShown, false);

    const anchorId = useId();

    const {
        getFloatingElProps,
        floatingElRef,
        arrow,
        getAnchorProps,
        ...rest
    } = useFloating({
        shown: open,
        setShown: setOpen,
        maxHeight,
        maxWidth,
        allowedPlacements,
        hoverEnabled: hoverEnabled ?? (hoverable || (open && hideOnBodyLeave)),
        clickEnabled,
        arrowEnabled,
        ...restProps,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _onKeyDown = (event: any) => {
        if (!hoverable) {
            return;
        }

        if (
            event?.key === 'Enter' || event?.key === ' '
        ) {

            if (shown) {
                return;
            }

            setOpen(true);

            // prevent default only for Space to avoid page scroll; Enter needs native behavior for interactive elements like links
            if (event?.key === ' ') {
                event.preventDefault();
            }
        }
    };

    return {
        ...rest,
        getDropdownProps: props => ({
            arrow,
            ...getFloatingElProps({
                'aria-labelledby': anchorId,
                ...props,
            }),
        }),
        getAnchorProps: props => getAnchorProps({
            id: anchorId,
            ...props,
            onKeyDown: event => {
                if (props?.onKeyDown) {
                    props.onKeyDown(event);
                }

                _onKeyDown(event);
            },
        }),
        dropdownRef: floatingElRef,
    };
};
